import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Typography,
  Box,
  Link,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store/store';
import { getQlikResources } from '../../../services/getQlikResources';
import { selectQlikCalcVariableByName } from '../../../store/slice/qlikVariableSlice';
import { getVariableContentByName, setVariableByName } from '../../../services/qlikVariableService';
import { QLIK_APP_REF } from '../../../constants';

interface RefineRecordsModalProps {
  open: boolean;
  onClose: () => void;
}

const RefineRecordsModal: React.FC<RefineRecordsModalProps> = ({ open, onClose }) => {
  const calcVariableName = 'calcVars2';
  const qlikCalcVariables = useSelector((state: RootState) => selectQlikCalcVariableByName(state, calcVariableName));
  
  const [formValues, setFormValues] = useState<Record<string, number | string>>({});
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
      const initialize = async () => {
        await loadVariables();
      };
      initialize();
  }, []);

  const loadVariables = async () => {
    try {
      
      // Load variable names from config
      const varsConfig = await getQlikResources('qlik-variables');
      const recVarsNames2 = varsConfig?.recVarsNames2 || [];
    //   setRecVarsNames(recVarsNames2);

      // Load each variable's content
      for (const varObj of recVarsNames2) {
        const varName = Object.keys(varObj)[0];
        const varConfig = varObj[varName];
        await getVariableContentByName(calcVariableName, varName, varConfig.appRef);
      }
    } catch (error) {
      console.error('Error loading variables:', error);
    }
  };

  useEffect(() => {
    if (open && qlikCalcVariables) {
      // Initialize form values from Qlik variables
      setFormValues({
        vC17B2B: qlikCalcVariables.vC17B2B ?? '',
        vC17IND: qlikCalcVariables.vC17IND ?? '',
        vC18B2B: qlikCalcVariables.vC18B2B ?? '',
        vC18IND: qlikCalcVariables.vC18IND ?? '',
        vC19B2B: qlikCalcVariables.vC19B2B ?? '',
        vC19IND: qlikCalcVariables.vC19IND ?? '',
        vC23B2B: qlikCalcVariables.vC23B2B ?? '',
        vC23IND: qlikCalcVariables.vC23IND ?? '',
        vC24B2B: qlikCalcVariables.vC24B2B ?? '',
        vC24IND: qlikCalcVariables.vC24IND ?? '',
        vC28IND: qlikCalcVariables.vC28IND ?? '',
        vC29IND: qlikCalcVariables.vC29IND ?? '',
        vC33B2B: qlikCalcVariables.vC33B2B ?? '',
        vC36IND: qlikCalcVariables.vC36IND ?? '',
        vC37B2B: qlikCalcVariables.vC37B2B ?? '',
      });
    }
  }, [open, qlikCalcVariables]);

  const handleInputChange = (varName: string, value: string) => {
    setFormValues(prev => ({ ...prev, [varName]: value }));
    setIsFormDirty(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    try {
      // Update all variables
      for (const [varName, value] of Object.entries(formValues)) {
        if (value !== '') {
          await setVariableByName(calcVariableName, varName, Number(value), QLIK_APP_REF, 'Risk Calculators');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
    
    onClose();
  };

  const handleCancel = () => {
    setIsFormDirty(false);
    onClose();
  };

  const isFormValid = () => {
    return (formValues.vC19B2B as number) > 0 && (formValues.vC24B2B as number) > 0;
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontStyle="italic">Refine Your Records Estimate</Typography>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Change any or all of the following defaults and click 'Calculate' to get a more accurate calculation for your specific use case.
        </Typography>

        <form onSubmit={handleSubmit}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '50%' }}></TableCell>
                  <TableCell sx={{ width: '25%', fontWeight: 'bold', fontSize: '1rem' }}>Business</TableCell>
                  <TableCell sx={{ width: '25%', fontWeight: 'bold', fontSize: '1rem' }}>Individuals</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Section 1: Electronic Sensitive Records */}
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body1" color='primary' sx={{ fontWeight: 600, mt: 1 }}>
                      Electronic Sensitive Records Associated with Your Sales
                    </Typography>
                  </TableCell>
                </TableRow>
                
                <TableRow>
                  <TableCell sx={{ typography: 'body1' }}>
                    What percentage of these transactions are recorded electronically? (E.g. credit/debit card, direct debit, ACH, EFT, etc)
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC17B2B}
                      onChange={(e) => handleInputChange('vC17B2B', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC17IND}
                      onChange={(e) => handleInputChange('vC17IND', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ typography: 'body1' }}>
                    What percentage of your customers do more than one transaction per year with you?
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC18B2B}
                      onChange={(e) => handleInputChange('vC18B2B', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC18IND}
                      onChange={(e) => handleInputChange('vC18IND', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ typography: 'body1' }}>
                    Of customers that do more than one transaction per year with you, what is the average number of transactions per year per customer?
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC19B2B}
                      onChange={(e) => handleInputChange('vC19B2B', e.target.value)}
                      placeholder="Change default"
                      required
                      error={!(formValues.vC19B2B as number > 0)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC19IND}
                      onChange={(e) => handleInputChange('vC19IND', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ typography: 'body1' }}>
                    For how many years of historical sales records do you maintain or have access to electronic records?
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC23B2B}
                      onChange={(e) => handleInputChange('vC23B2B', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC23IND}
                      onChange={(e) => handleInputChange('vC23IND', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ typography: 'body1' }}>
                    For how many years on average do you retain repeat customers?
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC24B2B}
                      onChange={(e) => handleInputChange('vC24B2B', e.target.value)}
                      placeholder="Change default"
                      required
                      error={!(formValues.vC24B2B as number > 0)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC24IND}
                      onChange={(e) => handleInputChange('vC24IND', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                </TableRow>

                {/* Section 2: Employees, Members, and Investors */}
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body1" color='primary' sx={{ fontWeight: 600, mt: 2 }}>
                      Sensitive Records Associated with Your Employees, Members, and Investors
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ typography: 'body1' }}>
                    For how many current and former employees, including family members, do you have sensitive records?
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC28IND}
                      onChange={(e) => handleInputChange('vC28IND', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ typography: 'body1' }}>
                    For how many current and former members and investors do you have sensitive records?
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC29IND}
                      onChange={(e) => handleInputChange('vC29IND', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                </TableRow>

                {/* Section 3: Vendors, Partners, Distributors */}
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body1" color='primary' sx={{ fontWeight: 600, mt: 2 }}>
                      Sensitive Records Associated with Vendors, Partners, Distributors, and Other Businesses You Work With
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ typography: 'body1' }}>
                    For how many other companies do you store confidential or proprietary information, whether or not subject to a formal NDA?
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC33B2B}
                      onChange={(e) => handleInputChange('vC33B2B', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>

                {/* Section 4: Transactional or Custodial Access */}
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body1" color='primary' sx={{ fontWeight: 600, mt: 2 }}>
                      Sensitive Records Associated with Your Transactional or Custodial Access to Third-Party Information
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ typography: 'body1' }}>
                    Other than as noted above, how many records containing nonpublic personal information do you store, transmit, or have access to?
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC36IND}
                      onChange={(e) => handleInputChange('vC36IND', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ typography: 'body1' }}>
                    Other than as noted above, how many other sensitive business records of others do you store, transmit, or have access to?
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formValues.vC37B2B}
                      onChange={(e) => handleInputChange('vC37B2B', e.target.value)}
                      placeholder="Change default"
                    />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              If you are a California resident, please{' '}
              <Link 
                href="https://www.chubb.com/us-en/online-privacy-policy.html#notice-at-collection" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                read our Notice at Collection.
              </Link>
            </Typography>
          </Box>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} variant="outlined" disabled={isSubmitting} sx={{textTransform: 'none'}}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          sx={{textTransform: 'none'}}
          variant="contained" 
          disabled={!isFormValid() || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? 'Calculating...' : 'Calculate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefineRecordsModal;
